import { useContext, useMemo, useState, KeyboardEvent } from 'react';
import FlyersContext from '@/context/FlyersContext';
import { FlyerType, APP_ROUTES } from '@/config/constants';
import campsIllustration from '@/assets/camps-illustration.svg';
import clubsIllustration from '@/assets/clubs-illustration.svg';
import summerProgramsIllustration from '@/assets/summer-programs-illustration.svg';
import communityFreeIllustration from '@/assets/explore_community_free.svg';
import { HeaderFixed, BottomViewFlyers, PageTitleHelmet, PeachjarLoader } from '@/components';
import { useNavigate } from 'react-router-dom';
import { NavbarSwitcher } from '@/design-system';
import { useAnalytics } from '@/hooks/useAnalytics';
import ParentGuide from './components/ParentGuide/ParentGuide';
import styles from './Home.styles';
import ProgramBanner from './components/ProgramBanner/ProgramBanner';
import FlyerCategoryList from './components/FlyerCategoryList/FlyerCategoryList';
import SubscriptionsBanner from './components/SubscriptionsBanner/SubscriptionsBanner';
import InfoBanner from './components/InfoBanner/InfoBanner';
import SubscriptionList from './components/SubscriptionList/SubscriptionList';
import { useQuery, useReactiveVar } from '@apollo/client';
import { GetFlyerDistributionInfoResponse, OwningTypeEnum } from '@/graphql/types';
import { GET_FLYER_DISTRIBUTION_INFO } from '@/graphql/queries';
import UserPreferencesContext from '@/context/UserPreferencesContext';
import { districtVar, subscriptionsVar } from '@/graphql/state';
import { EventDetails, TrackingEvents, TrackingStage } from '@/config/tracking';
import { useLandingPageTracker, useNavigateWithTracking } from '@/hooks';
import AllFlyersBanner from './components/AllFlyersBanner/AllFlyersBanner';

export const Home: React.FC = () => {
  const navigateWithTracking = useNavigateWithTracking();
  const { trackEvent } = useAnalytics();
  const [textSearch, setTextSearch] = useState<string>('');
  const district = useReactiveVar(districtVar);
  const subscriptions = useReactiveVar(subscriptionsVar);
  const { community } = useContext(UserPreferencesContext);
  const { activeTab, onChangeFlyerType, isSchoolTabSelected, targetTab } =
    useContext(FlyersContext);
  const { data: flyerDistributionInfoData, loading } = useQuery<GetFlyerDistributionInfoResponse>(
    GET_FLYER_DISTRIBUTION_INFO,
    {
      variables: {
        input: {
          owningType:
            activeTab === FlyerType.school ? OwningTypeEnum.district : OwningTypeEnum.organization,
        },
      },
    }
  );

  useLandingPageTracker(() => {
    trackEvent({
      event: TrackingEvents.browseContent,
      event_detail: EventDetails.browseContent(activeTab),
      stage: TrackingStage.discovery,
    });
  });

  const trackingFrom = {
    event_from: TrackingEvents.browseContent,
    event_detail_from: EventDetails.browseContent(activeTab),
    stage_from: TrackingStage.discovery,
  };

  const trackingData = useMemo(
    () => ({
      event: TrackingEvents.browseContent,
      event_detail: EventDetails.browseContent(targetTab),
      stage: TrackingStage.discovery,
      ...trackingFrom,
    }),
    [targetTab, activeTab]
  );

  const newDistribution = flyerDistributionInfoData?.getFlyerDistributionInfo.newDistribution;
  const upcomingDistribution =
    flyerDistributionInfoData?.getFlyerDistributionInfo.upcomingDistribution;

  const schools = useMemo(() => subscriptions.filter(s => s.type === 'school'), [subscriptions]);

  const hasMultipleSchools = schools.length > 1;
  const schoolName = hasMultipleSchools ? 'My Schools' : schools.length > 0 ? schools[0].name : '';

  const schoolNames = useMemo(() => schools.map(school => school.name, []), [schools]);

  const goToExploreFlyers = (type: string) => {
    navigateWithTracking(`/explore/${type}?tab=${activeTab}`, trackingFrom);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && textSearch.length > 0) {
      const encodedSearch = encodeURIComponent(textSearch);
      navigateWithTracking(`/explore/${encodedSearch}/results?tab=${activeTab}`, trackingFrom);
    }
  };

  const exploreBottomLabel = useMemo(() => {
    let selectedType = 'Community';
    if (activeTab === FlyerType.community) {
      if (schools.length > 1) {
        selectedType = 'Schools';
      } else {
        selectedType = 'School';
      }
    }
    return `Explore My ${selectedType}`;
  }, [activeTab, schools]);

  const onNewFlyersButtonClick = () => {
    goToExploreFlyers('new');
  };

  const onCommFreeFlyersButtonClick = () => {
    navigateWithTracking(APP_ROUTES.commFreeFlyerboard, trackingFrom);
  };

  if (loading) return <PeachjarLoader />;

  return (
    <div data-testid="explorePage" className={styles.background[activeTab]}>
      <PageTitleHelmet>Home</PageTitleHelmet>
      <HeaderFixed>
        <NavbarSwitcher
          trackId="explore_page"
          textSearch={textSearch}
          showSearchBar
          setTextSearch={setTextSearch}
          onKeyDown={onKeyDown}
          saveSearchVisible={false}
          trackingData={trackingData}
        />
      </HeaderFixed>
      <div className={styles.container} data-testid="exploreContent">
        <div className={styles.horizontalPadding}>
          <SubscriptionsBanner
            type={isSchoolTabSelected ? 'school' : 'district'}
            institutionName={isSchoolTabSelected ? schoolName : `${community.localArea}`}
            schools={schoolNames}
          />
          {newDistribution && (
            <InfoBanner
              isNewFlyers
              type={activeTab}
              onClick={onNewFlyersButtonClick}
              unreadExpiringAmount={newDistribution.unreadFlyers}
              updateAt={newDistribution.lastUpdate}
              dataTestId="NewFlyersBanner"
            />
          )}
          <FlyerCategoryList type={activeTab} />
          <div data-testid="banners">
            {isSchoolTabSelected ? (
              <>
                {/* <ProgramBanner
                  name="Clubs"
                  description="Discover a school club of interest"
                  illustration={clubsIllustration}
                  onClick={() => console.log('redirect to clubs')}
                /> */}
              </>
            ) : (
              <>
                {/* <ProgramBanner
                  name="Camps"
                  description="Discover an ideal camp in your community"
                  illustration={campsIllustration}
                  onClick={() => console.log('redirect to camps')}
                />
                <ProgramBanner
                  name="Summer Programs"
                  description="Discover exciting opportunities for summer"
                  illustration={summerProgramsIllustration}
                  onClick={() => console.log('redirect to summer programs')}
                /> */}
                <ProgramBanner
                  name="Free Things to Do"
                  description="Discover a free community opportunity"
                  illustration={communityFreeIllustration}
                  onClick={onCommFreeFlyersButtonClick}
                />
              </>
            )}
            <AllFlyersBanner
              activeTab={activeTab}
              isSchoolTab={isSchoolTabSelected}
              trackingFrom={trackingFrom}
            />
          </div>
        </div>
        {/* {flyerType === FlyerType.community && <ParentGuide />} */}
        <BottomViewFlyers
          label={exploreBottomLabel}
          onClick={() => {
            onChangeFlyerType(trackingData);
          }}
          variant={FlyerType[activeTab]}
        />
      </div>
    </div>
  );
};
